import AppointmentUSPBlock from '@components/AppointmentUSPBlock'
import {
  FloatingServiceMenu,
  Footer,
  Header,
  Page,
  PageTransition,
  pickSeoProps,
} from '@components/Page'
import ProductImage from '@components/_giftcard-detail/ProductImage'
import ProductInformation from '@components/_giftcard-detail/ProductInformation'
import ProductTabs from '@components/_giftcard-detail/ProductTabs'
import SaveRecentlyViewed from '@components/_product-detail/SaveRecentlyViewed'
import { Box } from '@gassan-ui'
import { Article, ArticleDocument } from '@generated'
import { buildLanguageAlternateNavigation } from '@lib/build-language-alternates'
import { genericPushToTrackingLayer } from '@lib/data-layer-social-ads/generic-push-to-tracking-layer'
import { getServerSideTranslations } from '@lib/get-server-side-translations'
import { initializeContactForm } from '@lib/initialize-contact-form'
import { SingleItemResponse, getPageByCodename } from '@lib/kontent'
import { GiftcardPage as GiftcardPageModel } from '@lib/kontent/models/GiftcardPage'
import pinterestTracking from '@lib/pinterest-tracking'
import { createUrqlClient } from '@lib/urqlClient'
import { GetStaticProps, NextPage } from 'next'
import { useRouter } from 'next/router'
import { rem } from 'polished'
import { useEffect } from 'react'

export const GIFTCARD_ID = '9810000'

type Props = SingleItemResponse<GiftcardPageModel> & {
  article: Article
  title: string | null
  description: string | null
  metaTitle: string | null
  customUsp: string | null
}

let timer

const GiftcardPage: NextPage<Props> = ({ item, article }) => {
  const router = useRouter()
  const { usp_line_1, usp_line_2, usp_line_3, usp_line_4 } = item.elements
  useEffect(() => {
    genericPushToTrackingLayer('Ecommerce', 'productImpressions')

    if (router.query.appointment) {
      timer = setTimeout(() => {
        initializeContactForm('appointment')
      }, 500)
    }

    pinterestTracking.pageVisit()

    return () => {
      clearTimeout(timer)
    }
  }, []) //eslint-disable-line

  let breadcrumbs = [
    {
      href: `/giftcard`,
      label: 'Giftcard',
    },
  ]

  return (
    <Page
      {...pickSeoProps(item.elements)}
      languageAlternates={buildLanguageAlternateNavigation(router.locales, 'giftcards')}
    >
      <FloatingServiceMenu />
      <SaveRecentlyViewed id={GIFTCARD_ID} />
      <Header />
      <PageTransition>
        <Box
          as="section"
          width={{
            _: '100%',
            large: 'calc(100% - 3rem)',
            xlarge: 'calc(100% - 5.5rem)',
          }}
          display="grid"
          gridTemplateColumns={{ _: '100%', large: `auto ${rem(360)}`, xlarge: `auto ${rem(420)}` }}
          gridColumnGap={{ large: rem(90), xlarge: rem(120) }}
          mt={{ _: 0, large: '2.5rem' }}
          mb={{ _: 0, large: '3rem' }}
          maxWidth="109rem"
          mx="auto"
        >
          <ProductImage
            alt="Giftcard product image"
            image={article.images[0]}
            breadcrumbItems={breadcrumbs}
          />
          <Box
            pt={{ _: '1.5rem', small: '3rem', large: 0 }}
            px={{ _: '1rem', small: '2rem', large: 0 }}
          >
            <ProductInformation
              title={item.elements.main_title.value}
              subtitle={item.elements.main_subtitle.value}
              usps={[usp_line_1.value, usp_line_2.value, usp_line_3.value, usp_line_4.value]}
              handleMakeAppointment={() => initializeContactForm('appointment')}
              handleGetInContact={() => initializeContactForm('contact')}
            />
          </Box>
        </Box>
        <ProductTabs
          description={item.elements.description_description.value}
          title={item.elements.description_title.value}
        />
        <AppointmentUSPBlock
          mt="5rem"
          subject={`Vraag over Giftcards`}
          variant="product-page"
          formType="default"
          productId={GIFTCARD_ID}
        />
        <Footer />
      </PageTransition>
    </Page>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale, preview }) => {
  const id = GIFTCARD_ID
  const client = createUrqlClient(locale)
  try {
    const content = await getPageByCodename<GiftcardPageModel>({
      codename: 'giftcard_page',
      lang: locale,
      preview,
    })
    const { data } = await client.query(ArticleDocument, { id }).toPromise()

    if (!data.article) {
      return { notFound: true }
    }

    return {
      props: {
        ...content,
        article: data.article,
        ...(await getServerSideTranslations(locale)),
      },
    }
  } catch (error) {
    console.error(`Error in getStaticProps for giftcard page (locale: ${locale}):`, error)
    return { notFound: true }
  }
}

export default GiftcardPage
